// PHASE_1 / B1 — leave feature API helpers (mutations + balances).
//
// The leave write path: file → submit (→ conflict warning) → approve/reject →
// cancel, plus the balance read. Explicit adapter signatures retain the local
// DTO boundary across the generated paths' request/response metadata.
//
// Hours are DECIMALS (`_hours`), never money — nothing here goes through
// <Money> (Phase 1 money/hours boundary).

import type { CalendarAccrualPeriod, LeaveSplitRule } from "@novemai/time-attendance-sdk";

import type { paths } from "@/api/schema.generated";
import { getAccessToken, platformGraph, platformGraphBaseUrl } from "@/api/platform-graph";
import {
  unwrapEnvelope,
  type EnvelopedFetchResult,
} from "@/api/errors";
import { type KeysetPage } from "@/api/keyset";

/** The server-authoritative approval step that scopes one decision retry. */
export interface LeaveDecisionContext {
  readonly approval_request_id: string;
  readonly current_step: number;
}

/** The leave-request row the API returns (documented shape from routes/leave.ts). */
export interface LeaveRequestRow {
  readonly request_id: string;
  readonly tenant_id: string;
  readonly employee_id: string;
  /** Subject's display name (SEM-301 join); null when unresolved/redacted. */
  readonly employee_name: string | null;
  readonly policy_id: string;
  /** Leave type's display name; null when unresolved, omitted on mutation echoes. */
  readonly policy_name?: string | null;
  readonly start_date: string;
  readonly end_date: string;
  readonly hours: string;
  readonly status: string;
  /** The requester's own explanation — never the approver's. */
  readonly reason: string | null;
  /** Why a rejected/cancelled request ended that way, in the decider's words. */
  readonly decision_reason?: string | null;
  /** ONE native delegation target (gap §9.1) — echoed back; null when none. */
  readonly delegate_employee_id: string | null;
  /**
   * Actor-current framework inbox rows carry the exact approval step. Legacy
   * rows and responses from a pre-context deployment may be null/omitted, so
   * callers must retain their legacy-safe fallback instead of inferring a step.
   */
  readonly decision_context?: LeaveDecisionContext | null;
  /**
   * The annual-leave record document of this request (LV-10): `issued` = the current
   * document, `voided` = it was withdrawn (cancelled / cut short by termination),
   * null = the request has none. Only the list read carries it; omitted on mutation
   * echoes and by an older deployment, where "no document" is the honest reading.
   */
  readonly leave_record_status?: "issued" | "voided" | null;
  /** Unpaid travel-leave days asked for on top of this annual request; omitted by an older deployment. */
  readonly travel_leave_days?: number;
  /** Set on the unpaid companion rows an approval writes for travel leave: the annual request they ride on. */
  readonly travel_parent_request_id?: string | null;
}

/** A balance row from GET /v1/leave/balances. Decimal hours, never money. */
export interface LeaveBalanceRow {
  readonly balance_id: string;
  readonly employee_id: string;
  /** Subject's display name (SEM-301 join); null when unresolved/redacted. */
  readonly employee_name: string | null;
  readonly policy_id: string;
  /** Policy display name (SEM-723 join); null when unresolved. No PII gate. */
  readonly policy_name: string | null;
  /** What is left to draw — already net of the hours held for pending requests. */
  readonly balance_hours: string;
  readonly cap_hours: string | null;
  /** Of the entitlement, the part carried over from a previous period. */
  readonly carryover_hours?: string | null;
  /**
   * The list read's breakdown: approved + taken hours, hours held by requests
   * still awaiting a decision, and their sum with `balance_hours`. Optional
   * because the write endpoints echo a balance row without it.
   */
  readonly used_hours?: string;
  readonly pending_hours?: string;
  readonly entitlement_hours?: string;
  /**
   * One line per service year the entitlement ledger has granted (oldest first);
   * `used_hours`, `entitlement_hours` and `carryover_hours` above describe the
   * LAST of them. Empty for a hand-keyed balance (no service-year structure to
   * invent); omitted by a pre-ledger deployment.
   */
  readonly per_service_year?: readonly LeaveServiceYearRow[];
  /**
   * false = the leave type draws no balance (unpaid leave); its row only exists so
   * the type shows in the request picker. Omitted by the write endpoints' echo and
   * by an older deployment: read as true there, which is what those rows always were.
   */
  readonly draws_balance?: boolean;
  /**
   * false = nothing has ever been granted, drawn or held on this account (a row
   * opened at 0 before the first anniversary). Same omission rule as above.
   */
  readonly entitlement_granted?: boolean;
  /**
   * Carried hours that burn if left unused, one entry per expiry day, nearest first.
   * Empty (or omitted by an older deployment / a write endpoint's echo) means nothing
   * can burn: the screen then shows NO line, never "0 hours".
   */
  readonly upcoming_burns?: readonly LeaveUpcomingBurn[];
  readonly status: string;
}

/** Hours of carried leave whose window closes on `expires_on` (YYYY-MM-DD). Decimal hours. */
export interface LeaveUpcomingBurn {
  readonly expires_on: string;
  readonly hours: string;
}

/** A service-year line of a ledger-backed balance (LV-11). Decimal hours. */
export interface LeaveServiceYearRow {
  readonly year_index: number;
  /** YYYY-MM-DD: the (clamped) anniversary the year's entitlement was granted on. */
  readonly anniversary_date: string;
  readonly granted_hours: string;
  readonly used_hours: string;
  /** Carried into this year from earlier ones: statutory days never burn. */
  readonly carried_hours: string;
}

/** A conflict overlap surfaced as a WARNING when a request is submitted. */
export interface ConflictOverlap {
  readonly request_id: string;
  readonly employee_id: string;
  readonly start_date: string;
  readonly end_date: string;
}

/** The submit response — the request + the conflict warning payload. */
export interface SubmitResult {
  readonly request: LeaveRequestRow;
  readonly conflict: {
    readonly has_conflict: boolean;
    readonly peak_simultaneous_absences: number;
    readonly overlaps: readonly ConflictOverlap[];
  };
}

/** The new leave request a file submission carries. */
export interface FileLeaveInput {
  readonly policy_id: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly partial_days?: readonly { readonly date: string; readonly start_time: string; readonly end_time: string }[];
  readonly reason?: string;
  /** ONE native delegation target (gap §9.1) — empty/absent = no delegate. */
  readonly delegate_employee_id?: string;
  /** Unpaid travel-leave days on top of an annual request (LV-10); absent = none. */
  readonly travel_leave_days?: number;
}

/** A contiguous run of dates the annual-leave split rule or travel leave reasons about. */
export interface LeaveDateRun {
  readonly start_date: string;
  readonly end_date: string;
  readonly days: number;
}

/**
 * What the annual-leave split rule (m.56 usage rule) says about a candidate request.
 * `violation` findings block only under `rule=enforce`; under `warn` they are shown and
 * nothing more, so the requester always has a way to file.
 */
export interface LeaveSplitCheck {
  readonly rule: LeaveSplitRule;
  readonly blocks: boolean;
  readonly service_year_index: number;
  readonly part_count: number;
  readonly longest_part_days: number;
  readonly remaining_days: number;
  readonly parts: readonly LeaveDateRun[];
  readonly findings: readonly {
    readonly code: "part_limit_exceeded" | "ten_day_part_unreachable" | "no_ten_day_part_yet";
    readonly severity: "violation" | "advisory";
  }[];
}

/** The read-only preview of a request: its duration plus what the usage rules make of it. */
export interface LeavePreview {
  readonly working_days: number;
  readonly hours: number;
  /** null = the rules do not apply (not the annual type, or the tenant switched them off). */
  readonly split_check?: LeaveSplitCheck | null;
  /** Where the requested unpaid travel leave would land; omitted by an older deployment. */
  readonly travel_leave?: { readonly days: number; readonly runs: readonly LeaveDateRun[] };
}

// ---- typed-cast call signatures for the consumed wire contracts --------

type PostFile = (
  path: "/v1/leave/requests",
  init: { body: FileLeaveInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<{ request: LeaveRequestRow }>>;

type PostSubmit = (
  path: "/v1/leave/requests/{id}/submit",
  init: { params: { path: { id: string } }; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<SubmitResult>>;

type PostDecision = (
  path: string,
  init: {
    params: { path: { id: string } };
    body?: { reason?: string };
    headers?: Record<string, string>;
  },
) => Promise<EnvelopedFetchResult<{ request: LeaveRequestRow }>>;

type PostApprove = (
  path: "/v1/leave/requests/{id}/approve",
  init: { params: { path: { id: string } }; headers: Record<string, string> },
) => Promise<
  EnvelopedFetchResult<{
    request: LeaveRequestRow;
    balance_impact: { impact_hours: number; projected_balance_hours: number };
  }>
>;

type GetBalances = (
  path: "/v1/leave/balances",
  init: { params: { query: { cursor?: string; mine?: boolean; employee_id?: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<LeaveBalanceRow>>>;

type GetRequests = (
  path: "/v1/leave/requests",
  init: { params: { query: { cursor?: string; employee_id: string } } },
) => Promise<EnvelopedFetchResult<KeysetPage<LeaveRequestRow>>>;

// ---- mutation + query functions -------------------------------------------

/** Read-only preview; no idempotency key or draft is created. */
export async function previewLeaveRequest(input: FileLeaveInput): Promise<LeavePreview> {
  const post = platformGraph.POST as unknown as (
    path: "/v1/leave/requests/preview", init: { body: FileLeaveInput },
  ) => Promise<EnvelopedFetchResult<LeavePreview>>;
  return unwrapEnvelope(post("/v1/leave/requests/preview", { body: input }));
}

/**
 * Download the record document of an annual-leave request as PDF.
 *
 * Not routed through the openapi-fetch client: that one unwraps the JSON envelope, and
 * this response is a raw PDF body (same reason as `downloadPayslipPdf`). The server
 * re-checks access and answers 404 for anyone outside owner / solid-line manager / HR.
 */
export async function downloadLeaveRecordPdf(
  requestId: string,
  filename: string,
  locale?: "en-US" | "tr-TR",
): Promise<void> {
  const token = getAccessToken();
  const headers: Record<string, string> = { accept: "application/pdf" };
  if (token !== null && token !== "") headers["authorization"] = `Bearer ${token}`;
  const query = locale === undefined ? "" : `?locale=${locale}`;
  const response = await fetch(
    `${platformGraphBaseUrl}/v1/leave/requests/${encodeURIComponent(requestId)}/record/pdf${query}`,
    { method: "GET", headers, credentials: "include" },
  );
  if (!response.ok) {
    throw new Error(`leave_record_pdf_failed:${response.status}`);
  }
  const url = URL.createObjectURL(await response.blob());
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function fileLeaveRequest(
  input: FileLeaveInput,
  idempotencyKey: string,
): Promise<LeaveRequestRow> {
  const post = platformGraph.POST as unknown as PostFile;
  const result = await unwrapEnvelope(
    post("/v1/leave/requests", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.request;
}

export async function submitLeaveRequest(
  id: string,
  idempotencyKey: string,
): Promise<SubmitResult> {
  const post = platformGraph.POST as unknown as PostSubmit;
  return unwrapEnvelope(
    post("/v1/leave/requests/{id}/submit", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

export async function approveLeaveRequest(
  id: string,
  idempotencyKey: string,
): Promise<LeaveRequestRow> {
  const post = platformGraph.POST as unknown as PostApprove;
  const result = await unwrapEnvelope(
    post("/v1/leave/requests/{id}/approve", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
  return result.request;
}

export async function rejectLeaveRequest(
  id: string,
  idempotencyKey: string,
  reason?: string,
): Promise<LeaveRequestRow> {
  const post = platformGraph.POST as unknown as PostDecision;
  const result = await unwrapEnvelope(
    post("/v1/leave/requests/{id}/reject", {
      params: { path: { id } },
      headers: { "x-idempotency-key": idempotencyKey },
      ...(reason !== undefined && { body: { reason } }),
    }),
  );
  return result.request;
}

export async function cancelLeaveRequest(id: string, reason?: string): Promise<LeaveRequestRow> {
  const post = platformGraph.POST as unknown as PostDecision;
  const result = await unwrapEnvelope(
    post("/v1/leave/requests/{id}/cancel", {
      params: { path: { id } },
      ...(reason !== undefined && { body: { reason } }),
    }),
  );
  return result.request;
}

export async function fetchLeaveBalancesPage(
  cursor: string | null,
  mine = false,
): Promise<KeysetPage<LeaveBalanceRow>> {
  const get = platformGraph.GET as unknown as GetBalances;
  return unwrapEnvelope(
    get("/v1/leave/balances", {
      params: {
        query: {
          ...(cursor !== null && { cursor }),
          ...(mine && { mine: true }),
        },
      },
    }),
  );
}

/**
 * One person's balances and requests (the personnel page). `employee_id` only
 * narrows: the server keeps an employee_self caller pinned to themselves and a
 * manager inside their subtree, whatever id is sent.
 */
export async function fetchEmployeeLeaveBalancesPage(
  employeeId: string,
  cursor: string | null,
): Promise<KeysetPage<LeaveBalanceRow>> {
  const get = platformGraph.GET as unknown as GetBalances;
  return unwrapEnvelope(
    get("/v1/leave/balances", {
      params: { query: { employee_id: employeeId, ...(cursor !== null && { cursor }) } },
    }),
  );
}

export async function fetchEmployeeLeaveRequestsPage(
  employeeId: string,
  cursor: string | null,
): Promise<KeysetPage<LeaveRequestRow>> {
  const get = platformGraph.GET as unknown as GetRequests;
  return unwrapEnvelope(
    get("/v1/leave/requests", {
      params: { query: { employee_id: employeeId, ...(cursor !== null && { cursor }) } },
    }),
  );
}

// ---- Unused-leave payout dues (4857 m.59) ----------------------------------

// Derived from the generated contract, like LeaveTypeRow: the row carries hours
// and a state only — no amount, because the amount is priced from the gross wage
// when the pay run pays it, so there is nothing to show but what the API knows.
export type PayoutDueRow = Readonly<
  paths["/v1/leave/payout-dues"]["get"]["responses"][200]["content"]["application/json"]["data"]["items"][number]
>;

type GetPayoutDues = (
  path: "/v1/leave/payout-dues",
  init: {
    params: {
      query: { employee_id: string; reason?: PayoutDueRow["reason"]; status?: PayoutDueRow["status"] };
    };
  },
) => Promise<EnvelopedFetchResult<{ readonly items: readonly PayoutDueRow[] }>>;

export async function fetchPayoutDues(input: {
  readonly employeeId: string;
  readonly reason?: PayoutDueRow["reason"];
}): Promise<readonly PayoutDueRow[]> {
  const get = platformGraph.GET as unknown as GetPayoutDues;
  const page = await unwrapEnvelope(
    get("/v1/leave/payout-dues", {
      params: {
        query: {
          employee_id: input.employeeId,
          ...(input.reason !== undefined && { reason: input.reason }),
        },
      },
    }),
  );
  return page.items;
}

// ---- Leave Types API (SEM-7200 / SEM-7202) --------------------------------

// Derived from the generated contract, not retyped: the hand-written copy of this
// row lacked `payroll_treatment` for a wave after the route started returning it,
// so the screen showed a sick-report type as plain "Paid" and nothing failed.
export type LeaveTypeRow = Readonly<
  paths["/v1/leave/types"]["get"]["responses"][200]["content"]["application/json"]["data"][number]
>;

export type PayrollTreatment = LeaveTypeRow["payroll_treatment"];

export interface CreateLeaveTypeInput {
  readonly code: string;
  readonly name: string;
  readonly unit: "hours" | "days";
  readonly paid: boolean;
  readonly payroll_treatment?: PayrollTreatment;
  readonly accrual_kind: "none" | "monthly" | "anniversary" | "statutory_tr";
  readonly accrual_amount_per_period?: string | null;
  readonly cap?: string | null;
  readonly carryover_cap?: string | null;
  readonly carryover_expires_months?: number | null;
  readonly negative_balance_allowed?: boolean;
  readonly requires_document?: boolean;
  readonly min_tenure_months?: number;
  /** LV-10 usage-rule settings of the statutory annual type; any other type must leave them at warn / 0. */
  readonly split_rule?: LeaveSplitRule;
  readonly travel_leave_max_days?: number;
  readonly statutory_ref?: string | null;
}

export interface PatchLeaveTypeInput {
  readonly name?: string;
  readonly paid?: boolean;
  readonly payroll_treatment?: PayrollTreatment;
  readonly accrual_kind?: "none" | "monthly" | "anniversary" | "statutory_tr";
  readonly accrual_amount_per_period?: string | null;
  readonly cap?: string | null;
  readonly carryover_cap?: string | null;
  readonly carryover_expires_months?: number | null;
  readonly negative_balance_allowed?: boolean;
  readonly requires_document?: boolean;
  readonly min_tenure_months?: number;
  readonly split_rule?: LeaveSplitRule;
  readonly travel_leave_max_days?: number;
  readonly statutory_ref?: string | null;
}

type GetLeaveTypes = (
  path: "/v1/leave/types",
  init?: { params?: { query?: { state?: "draft" | "active" | "archived" | "all" } } },
) => Promise<EnvelopedFetchResult<readonly LeaveTypeRow[]>>;

type PostLeaveType = (
  path: "/v1/leave/types",
  init: { body: CreateLeaveTypeInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<LeaveTypeRow>>;

type PatchLeaveType = (
  path: "/v1/leave/types/{leave_type_id}",
  init: { params: { path: { leave_type_id: string } }; body: PatchLeaveTypeInput },
) => Promise<EnvelopedFetchResult<LeaveTypeRow>>;

type PostArchiveLeaveType = (
  path: "/v1/leave/types/{leave_type_id}/archive",
  init: { params: { path: { leave_type_id: string } }; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<LeaveTypeRow>>;

export async function fetchLeaveTypes(
  state?: "draft" | "active" | "archived" | "all",
): Promise<readonly LeaveTypeRow[]> {
  const get = platformGraph.GET as unknown as GetLeaveTypes;
  return unwrapEnvelope(
    get("/v1/leave/types", {
      params: {
        query: state ? { state } : {},
      },
    }),
  );
}

export async function createLeaveType(
  input: CreateLeaveTypeInput,
  idempotencyKey: string,
): Promise<LeaveTypeRow> {
  const post = platformGraph.POST as unknown as PostLeaveType;
  return unwrapEnvelope(
    post("/v1/leave/types", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

export async function patchLeaveType(
  leaveTypeId: string,
  input: PatchLeaveTypeInput,
): Promise<LeaveTypeRow> {
  const patch = platformGraph.PATCH as unknown as PatchLeaveType;
  return unwrapEnvelope(
    patch("/v1/leave/types/{leave_type_id}", {
      params: { path: { leave_type_id: leaveTypeId } },
      body: input,
    }),
  );
}

export async function archiveLeaveType(
  leaveTypeId: string,
  idempotencyKey: string,
): Promise<LeaveTypeRow> {
  const post = platformGraph.POST as unknown as PostArchiveLeaveType;
  return unwrapEnvelope(
    post("/v1/leave/types/{leave_type_id}/archive", {
      params: { path: { leave_type_id: leaveTypeId } },
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

// ---- Leave Balances Write API (SEM-7201 / SEM-7202) ------------------------

// Create and bulk take an OMITTED field, never `null`: the route's schema types
// each optional field as its value or absent, so a `null` there is a 400 — that is
// how every "Bakiye Tanımla" from this screen failed while the component tests
// (which mock the POST) stayed green. Only PATCH reads `null` as "clear it".
//
// `accrual_period` is the calendar subset: the route accepts per_pay_period and
// per_hour_worked too, but no job advances them, so a form that offers them
// creates a balance that never accrues.
export interface CreateBalanceInput {
  readonly employee_id: string;
  readonly policy_id: string;
  readonly balance_hours: number;
  readonly accrual_rate_hours_per_period?: number;
  readonly accrual_period?: CalendarAccrualPeriod;
  readonly cap_hours?: number;
  readonly carryover_hours?: number;
  readonly notes?: string;
}

export interface PatchBalanceInput {
  readonly balance_hours?: number;
  readonly accrual_rate_hours_per_period?: number | null;
  readonly accrual_period?: CalendarAccrualPeriod | null;
  readonly cap_hours?: number | null;
  readonly carryover_hours?: number | null;
  readonly notes?: string | null;
  readonly status?: "active" | "suspended" | "closed";
}

export interface BulkCreateBalancesInput {
  readonly policy_id: string;
  readonly scope_kind: "all" | "department" | "location" | "legal_entity";
  readonly scope_id?: string;
  readonly balance_hours: number;
  readonly accrual_rate_hours_per_period?: number;
  readonly accrual_period?: CalendarAccrualPeriod;
  readonly cap_hours?: number;
  readonly carryover_hours?: number;
  readonly notes?: string;
}

export interface BulkCreateBalancesResult {
  readonly affected_count: number;
}

type PostCreateBalance = (
  path: "/v1/leave/balances",
  init: { body: CreateBalanceInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<LeaveBalanceRow>>;

type PatchBalance = (
  path: "/v1/leave/balances/{balanceId}",
  init: { params: { path: { balanceId: string } }; body: PatchBalanceInput },
) => Promise<EnvelopedFetchResult<LeaveBalanceRow>>;

type PostBulkBalances = (
  path: "/v1/leave/balances/bulk",
  init: { body: BulkCreateBalancesInput; headers: Record<string, string> },
) => Promise<EnvelopedFetchResult<BulkCreateBalancesResult>>;

export async function createLeaveBalance(
  input: CreateBalanceInput,
  idempotencyKey: string,
): Promise<LeaveBalanceRow> {
  const post = platformGraph.POST as unknown as PostCreateBalance;
  return unwrapEnvelope(
    post("/v1/leave/balances", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

export async function patchLeaveBalance(
  balanceId: string,
  input: PatchBalanceInput,
): Promise<LeaveBalanceRow> {
  const patch = platformGraph.PATCH as unknown as PatchBalance;
  return unwrapEnvelope(
    patch("/v1/leave/balances/{balanceId}", {
      params: { path: { balanceId } },
      body: input,
    }),
  );
}

export async function bulkCreateLeaveBalances(
  input: BulkCreateBalancesInput,
  idempotencyKey: string,
): Promise<BulkCreateBalancesResult> {
  const post = platformGraph.POST as unknown as PostBulkBalances;
  return unwrapEnvelope(
    post("/v1/leave/balances/bulk", {
      body: input,
      headers: { "x-idempotency-key": idempotencyKey },
    }),
  );
}

